import { Option } from '@vaaas/fpts/option';

/** Implementation of a hash table. Based on: http://www.mojavelinux.com/articles/javascript_hashes.html
 *
 * DEPRECATED! Please use a Map instead!
 */
export default class HashTable<K, V> {
    length: number;
    items: Partial<Record<string, V>>;
    keysArray: K[];

    constructor() {
        this.length = 0;
        this.items = {};
        this.keysArray = [];
    }

    setItem(key: K, value: V): Option<V> {
        let previous = undefined;
        if (this.hasItem(key)) {
            previous = this.items[key as string];
            const x = this.keysArray.indexOf(key);
            this.keysArray.splice(x, 1);
        } else {
            this.length++;
        }
        this.items[key as string] = value;
        this.keysArray.push(key);
        return previous;
    }

    getItem(k: K): Option<V> {
        return this.hasItem(k)
            ? this.items[k as string]
            : undefined;
    }

    hasItem(k: K): boolean {
        return this.items.hasOwnProperty(k as string);
    }

    removeItem(key: K): Option<V> {
        if (this.hasItem(key)) {
            const previous = this.items[key as string];
            this.length--;
            delete this.items[key as string];
            const index = this.keysArray.indexOf(key);
            this.keysArray.splice(index, 1);
            return previous;
        } else {
            return undefined;
        }
    }

    keys(): Array<K> {
        return Array.from(this.keysArray);
    }

    values(): Array<V> {
        return this.keysArray.reduce((xs, x) => {
            xs.push(this.items[x as string]!);
            return xs;
        }, [] as V[]);
    }

    entries(): Array<[K, V]> {
        const xs = [];
        for (const k of this.keysArray)
            xs.push([k, this.items[k as string]!] as [K, V]);
        return xs;
    }

    getKeyAtIndex(index: number): Option<K> {
        return this.keysArray[index];
    }

    getItemAtIndex(index: number): Option<V> {
        const k = this.getKeyAtIndex(index);
        if (k)
            return this.items[k as string];
        else
            return undefined;
    }

    each(f: (k: K, v: V) => void): void {
        for (const k of this.keysArray)
            f(k, this.items[k as string]!);
    }

    firstKey(): Option<K> {
        return this.keysArray[0];
    }

    firstValue(): Option<V> {
        const k = this.firstKey();
        if (k)
            return this.items[k as string];
        else
            return undefined;
    }

    clear(): void {
        this.items = {};
        this.keysArray = [];
        this.length = 0;
    }
}

export const get_from = <K, V>(xs: HashTable<K, V>) => (x: K) => xs.getItem(x);
